/**
 * SmartFlowsList — Lista de Smart Flows con design language unificado.
 *
 * Adopta el mismo patrón visual que `emr/ListaPacientesView.jsx`:
 *   - Header h5 navy + subtítulo
 *   - Búsqueda dominante con borderRadius 2.5
 *   - Filter chips segmented (gradient navy en activo)
 *   - Lista en Paper con avatars circulares (gradient determinístico por nombre)
 *   - Botón primario navy gradient
 *
 * Lógica preservada: search, filtros, sort, selección múltiple, plantillas,
 * favoritos, exportar config, renombrar inline, atajos de teclado.
 * Eliminado: vista grid (queda solo list); FlowCard.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Chip, CircularProgress, Alert,
  TextField, InputAdornment, IconButton, Menu, MenuItem, Button,
  Checkbox, Fade, Tooltip, Switch, Avatar,
} from "@mui/material";
import {
  SmartToy as SmartToyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as ConfigIcon,
  Sort as SortIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  SelectAll as SelectAllIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FileDownload as ExportIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  SortByAlpha as SortByAlphaIcon,
  AccessTime as AccessTimeIcon,
  FiberNew as FiberNewIcon,
} from "@mui/icons-material";
import { StyledDialog, inputStyles } from "../common";
import PlantillasGaleria from "./PlantillasGaleria";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";

const API_URL = "/api";

// Paleta de avatares (8 colores estables hasheados por nombre del chatbot)
const AVATAR_COLORS = [
  { bg: "#e0e7ff", fg: "#3730a3" },  // indigo
  { bg: "#fce7f3", fg: "#9f1239" },  // rosa
  { bg: "#cffafe", fg: "#155e75" },  // cyan
  { bg: "#dcfce7", fg: "#166534" },  // verde
  { bg: "#fef3c7", fg: "#92400e" },  // ambar
  { bg: "#ede9fe", fg: "#5b21b6" },  // morado
  { bg: "#dbeafe", fg: "#1e40af" },  // azul
  { bg: "#fee2e2", fg: "#991b1b" },  // rojo
];

function hashColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function iniciales(nombre) {
  if (!nombre) return "?";
  const parts = nombre.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function timeAgo(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return "hace un momento";
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `hace ${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `hace ${Math.floor(seconds / 86400)}d`;
  if (seconds < 2592000) return `hace ${Math.floor(seconds / 604800)}sem`;
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

function isRecentlyActive(dateString) {
  if (!dateString) return false;
  return (new Date() - new Date(dateString)) < 3600000;
}

function getUsageLevel(used, limit) {
  if (!limit || !used) return null;
  const pct = (used / limit) * 100;
  if (pct >= 95) return "critical";
  if (pct >= 80) return "warning";
  return "normal";
}

const USAGE_COLORS = { warning: "#f59e0b", critical: "#ef4444" };

const ORDENES = [
  { id: "last_activity",  label: "Última actividad", icono: <AccessTimeIcon sx={{ fontSize: 16 }} /> },
  { id: "name",           label: "Alfabético",       icono: <SortByAlphaIcon sx={{ fontSize: 16 }} /> },
  { id: "created",        label: "Más reciente",     icono: <FiberNewIcon sx={{ fontSize: 16 }} /> },
];

const SEGMENTOS = [
  { id: "all",       label: "Todos",     hint: "Todos los chatbots" },
  { id: "active",    label: "Activos",   hint: "Con el agente IA habilitado" },
  { id: "inactive",  label: "Inactivos", hint: "Con el agente IA pausado" },
  { id: "whatsapp",  label: "Con WhatsApp", hint: "Conectados a un número de WhatsApp" },
  { id: "no_config", label: "Sin configurar", hint: "Aún sin agente configurado" },
  { id: "favorites", label: "Favoritos", hint: "Marcados como favoritos" },
];

export default function SmartFlowsList() {
  const navigate = useNavigate();
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("last_activity");
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);

  // Crear chatbot
  const [createDialog, setCreateDialog] = useState(false);
  const [galeriaOpen, setGaleriaOpen] = useState(false);
  const [newChatbotName, setNewChatbotName] = useState("");
  const [creating, setCreating] = useState(false);

  // Selección múltiple
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectionActive = selectedIds.size > 0;

  // Eliminar
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Duplicar
  const [duplicateDialog, setDuplicateDialog] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicating, setDuplicating] = useState(false);

  // Renombrar inline
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // Favoritos
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("smartflows_favorites") || "[]"); }
    catch { return []; }
  });

  useEffect(() => { fetchChatbots(); }, []);
  useEffect(() => {
    localStorage.setItem("smartflows_favorites", JSON.stringify(favorites));
  }, [favorites]);

  const fetchChatbots = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/smart-flows/chatbots`);
      if (!response.ok) throw new Error("Error cargando chatbots");
      setChatbots(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChatbot = async () => {
    if (!newChatbotName.trim()) return;
    setCreating(true);
    try {
      const userId = localStorage.getItem("user_id");
      const params = new URLSearchParams({ name: newChatbotName.trim(), user_id: userId });
      const response = await fetch(`${API_URL}/smart-flows/chatbots?${params}`, { method: "POST" });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Error al crear chatbot"); }
      const data = await response.json();
      setCreateDialog(false);
      setNewChatbotName("");
      navigate(`/smart-flows/${data.chatbot.id}`);
    } catch (err) { setError(err.message); }
    finally { setCreating(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const idsToDelete = deleteTarget ? [deleteTarget.id] : Array.from(selectedIds);
      for (const id of idsToDelete) {
        const res = await fetch(`${API_URL}/flows/${id}`, { method: "DELETE" });
        if (!res.ok) { const err = await res.json(); throw new Error(err.detail || `Error eliminando chatbot ${id}`); }
      }
      setChatbots((prev) => prev.filter((b) => !idsToDelete.includes(b.id)));
      setSelectedIds(new Set());
      setDeleteDialog(false);
      setDeleteTarget(null);
    } catch (err) { setError(err.message); }
    finally { setDeleting(false); }
  };

  const handleToggleEnabled = useCallback(async (chatbotId) => {
    try {
      const res = await fetch(`${API_URL}/smart-flows/${chatbotId}/toggle-enabled`, { method: "PUT" });
      if (!res.ok) throw new Error("Error al cambiar estado");
      const data = await res.json();
      setChatbots((prev) => prev.map((b) => b.id === chatbotId ? { ...b, agent_enabled: data.enabled } : b));
    } catch (err) { setError(err.message); }
  }, []);

  const handleDuplicate = async () => {
    if (!duplicateTarget) return;
    setDuplicating(true);
    try {
      const res = await fetch(`${API_URL}/smart-flows/${duplicateTarget.id}/duplicate`, { method: "POST" });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Error al duplicar"); }
      setDuplicateDialog(false);
      setDuplicateTarget(null);
      await fetchChatbots();
    } catch (err) { setError(err.message); }
    finally { setDuplicating(false); }
  };

  const handleStartRename = useCallback((chatbot) => {
    setRenamingId(chatbot.id);
    setRenameValue(chatbot.name);
  }, []);
  const handleCancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue("");
  }, []);
  const handleSaveRename = useCallback(async (chatbotId) => {
    if (renamingId === null) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed.length > 100) { handleCancelRename(); return; }
    setRenaming(true);
    try {
      const res = await fetch(`${API_URL}/smart-flows/${chatbotId}/rename`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      if (!res.ok) throw new Error("Error al renombrar");
      const data = await res.json();
      setChatbots((prev) => prev.map((b) => b.id === chatbotId ? { ...b, name: data.name } : b));
    } catch (err) { setError(err.message); }
    finally { setRenaming(false); handleCancelRename(); }
  }, [renameValue, renamingId, handleCancelRename]);

  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) => prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]);
  }, []);

  const handleExport = useCallback(async (chatbot) => {
    try {
      const res = await fetch(`${API_URL}/smart-flows/${chatbot.id}`);
      if (!res.ok) throw new Error("Error obteniendo configuración");
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${chatbot.name.replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ\s-]/g, "").replace(/\s+/g, "_")}_config.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Filtrado + ordenamiento (lógica preservada del archivo anterior)
  const filteredAndSortedChatbots = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let result = chatbots.filter((bot) => {
      const matchesSearch = bot.name.toLowerCase().includes(term)
        || (bot.whatsapp_number && bot.whatsapp_number.toLowerCase().includes(term));
      let matches = true;
      if (filter === "active") matches = bot.agent_enabled;
      else if (filter === "inactive") matches = !bot.agent_enabled;
      else if (filter === "favorites") matches = favorites.includes(bot.id);
      else if (filter === "whatsapp") matches = !!bot.whatsapp_number;
      else if (filter === "no_config") matches = !bot.has_config;
      return matchesSearch && matches;
    });

    result.sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "last_activity") return (b.last_activity ? new Date(b.last_activity) : new Date(0)) - (a.last_activity ? new Date(a.last_activity) : new Date(0));
      if (sortBy === "created") return (b.created_at ? new Date(b.created_at) : new Date(0)) - (a.created_at ? new Date(a.created_at) : new Date(0));
      return 0;
    });

    // Favoritos siempre primero
    result.sort((a, b) => {
      const aFav = favorites.includes(a.id) ? 1 : 0;
      const bFav = favorites.includes(b.id) ? 1 : 0;
      return bFav - aFav;
    });
    return result;
  }, [chatbots, searchTerm, filter, sortBy, favorites]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === filteredAndSortedChatbots.length) return new Set();
      return new Set(filteredAndSortedChatbots.map((b) => b.id));
    });
  }, [filteredAndSortedChatbots]);

  // Conteo por segmento (para badges)
  const conteos = useMemo(() => ({
    all:        chatbots.length,
    active:     chatbots.filter((b) => b.agent_enabled).length,
    inactive:   chatbots.filter((b) => !b.agent_enabled).length,
    whatsapp:   chatbots.filter((b) => !!b.whatsapp_number).length,
    no_config:  chatbots.filter((b) => !b.has_config).length,
    favorites:  favorites.length,
  }), [chatbots, favorites]);

  const cardActions = useCallback((chatbot) => ({
    selected: selectedIds.has(chatbot.id),
    selectionActive,
    onSelect: () => toggleSelect(chatbot.id),
    onDelete: () => { setDeleteTarget({ id: chatbot.id, name: chatbot.name }); setDeleteDialog(true); },
    onDuplicate: () => { setDuplicateTarget({ id: chatbot.id, name: chatbot.name }); setDuplicateDialog(true); },
    onToggle: () => handleToggleEnabled(chatbot.id),
    onClick: () => navigate(`/smart-flows/${chatbot.id}`),
    renamingId, renameValue, renaming,
    onRenameChange: setRenameValue,
    onStartRename: () => handleStartRename(chatbot),
    onSaveRename: () => handleSaveRename(chatbot.id),
    onCancelRename: handleCancelRename,
    isFavorite: favorites.includes(chatbot.id),
    onToggleFavorite: () => toggleFavorite(chatbot.id),
    onExport: () => handleExport(chatbot),
  }), [selectedIds, selectionActive, toggleSelect, handleToggleEnabled, navigate,
    renamingId, renameValue, renaming, handleStartRename, handleSaveRename, handleCancelRename,
    favorites, toggleFavorite, handleExport]);

  // Atajos de teclado
  useEffect(() => {
    const handler = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const mod = isMac ? e.metaKey : e.ctrlKey;
      const tag = e.target.tagName;
      const isInput = tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable;
      if (e.key === "Escape") {
        if (renamingId) { handleCancelRename(); return; }
        if (createDialog || deleteDialog || duplicateDialog) return;
        if (selectionActive) { clearSelection(); e.preventDefault(); return; }
      }
      if (isInput) return;
      if (mod && e.key === "n") { e.preventDefault(); setCreateDialog(true); }
      if ((e.key === "Delete" || e.key === "Backspace") && selectionActive) {
        e.preventDefault();
        setDeleteTarget(null); setDeleteDialog(true);
      }
      if (mod && e.key === "a") { e.preventDefault(); toggleSelectAll(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [renamingId, handleCancelRename, createDialog, deleteDialog, duplicateDialog, selectionActive, clearSelection, toggleSelectAll]);

  const ordenActual = ORDENES.find((o) => o.id === sortBy) || ORDENES[0];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e3a5f", mb: 0.3 }}>
            Smart Flows
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
            {filteredAndSortedChatbots.length} de {chatbots.length} chatbot{chatbots.length === 1 ? "" : "s"}
            {filter !== "all" ? ` · filtro: ${SEGMENTOS.find((s) => s.id === filter)?.label || filter}` : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined" size="small"
            startIcon={<MenuBookRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setGaleriaOpen(true)}
            sx={{
              borderColor: "#c7d2fe", color: "#4f46e5",
              textTransform: "none", fontSize: 12, fontWeight: 600,
              borderRadius: 2, px: 1.75, py: 0.7,
              "&:hover": { borderColor: "#6366f1", bgcolor: "#eef2ff" },
            }}
          >
            Plantillas
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
            sx={{
              background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
              borderRadius: 2,
              px: 2.5, py: 0.8,
              fontSize: 13, fontWeight: 700, textTransform: "none",
              boxShadow: "0 4px 12px rgba(30,58,95,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)" },
            }}
          >
            Nuevo chatbot
          </Button>
        </Box>
      </Box>

      {/* Búsqueda dominante */}
      <TextField
        fullWidth
        placeholder="Buscar por nombre del chatbot o número de WhatsApp..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5, background: "#fff", fontSize: 14,
            "& fieldset": { borderColor: "#e2e8f0" },
            "&:hover fieldset": { borderColor: "#cbd5e1" },
            "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: 2 },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#94a3b8" }} />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm("")}>
                <ClearIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Toolbar: segmentos + sort */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        mb: 1.5, flexWrap: "wrap", gap: 1,
      }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, flex: 1 }}>
          {SEGMENTOS.map((s) => {
            const activo = filter === s.id;
            const count = conteos[s.id] || 0;
            // Solo mostrar Favoritos si hay alguno
            if (s.id === "favorites" && favorites.length === 0) return null;
            return (
              <Tooltip key={s.id} title={s.hint || ""} arrow placement="top">
                <Chip
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span>{s.label}</span>
                      <Box sx={{
                        fontSize: 10, fontWeight: 700,
                        bgcolor: activo ? "rgba(255,255,255,0.25)" : "#e2e8f0",
                        color: activo ? "#fff" : "#475569",
                        px: 0.7, py: 0.05, borderRadius: 1,
                      }}>
                        {count}
                      </Box>
                    </Box>
                  }
                  size="small"
                  onClick={() => setFilter(s.id)}
                  sx={{
                    fontSize: 12, fontWeight: 600, height: 28, cursor: "pointer",
                    color: activo ? "#fff" : "#475569",
                    background: activo ? "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)" : "#fff",
                    border: activo ? "none" : "1px solid #e2e8f0",
                    "&:hover": {
                      background: activo
                        ? "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)"
                        : "#f8fafc",
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>

        <Button
          size="small"
          onClick={(e) => setSortMenuAnchor(e.currentTarget)}
          startIcon={ordenActual.icono}
          sx={{
            textTransform: "none", fontSize: 12, fontWeight: 600,
            color: "#475569", borderRadius: 2,
            border: "1px solid #e2e8f0", background: "#fff",
            "&:hover": { background: "#f8fafc" },
          }}
        >
          {ordenActual.label}
        </Button>
        <Menu
          anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={() => setSortMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: 2, mt: 0.5, minWidth: 200 } }}
        >
          {ORDENES.map((o) => (
            <MenuItem
              key={o.id}
              selected={o.id === sortBy}
              onClick={() => { setSortBy(o.id); setSortMenuAnchor(null); }}
              sx={{ fontSize: 13, gap: 1 }}
            >
              <Box sx={{ color: o.id === sortBy ? "#1e3a5f" : "#94a3b8" }}>{o.icono}</Box>
              {o.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, py: 0.5, borderRadius: 2 }}>{error}</Alert>}

      {/* Toolbar de selección múltiple */}
      <Fade in={selectionActive}>
        <Box sx={{
          display: selectionActive ? "flex" : "none", alignItems: "center", gap: 2,
          mb: 1.5, px: 2, py: 1, borderRadius: 2,
          background: "#fff", border: "1px solid #c7d2fe",
          boxShadow: "0 2px 8px rgba(99,102,241,0.08)",
        }}>
          <Typography sx={{ color: "#4f46e5", fontSize: 13, fontWeight: 700 }}>
            {selectedIds.size} seleccionado{selectedIds.size > 1 ? "s" : ""}
          </Typography>
          <Button size="small" startIcon={<SelectAllIcon sx={{ fontSize: 14 }} />} onClick={toggleSelectAll}
            sx={{ color: "#4f46e5", fontSize: 12, textTransform: "none", "&:hover": { bgcolor: "#eef2ff" } }}>
            {selectedIds.size === filteredAndSortedChatbots.length ? "Deseleccionar" : "Seleccionar todos"}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<DeleteIcon sx={{ fontSize: 14 }} />} onClick={() => { setDeleteTarget(null); setDeleteDialog(true); }}
            sx={{ color: "#dc2626", fontSize: 12, fontWeight: 700, textTransform: "none", bgcolor: "#fef2f2", borderRadius: 2, px: 1.5, "&:hover": { bgcolor: "#fee2e2" } }}>
            Eliminar ({selectedIds.size})
          </Button>
          <IconButton size="small" onClick={clearSelection} sx={{ color: "#94a3b8", "&:hover": { color: "#64748b" } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Fade>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress sx={{ color: "#1e3a5f" }} />
        </Box>
      ) : filteredAndSortedChatbots.length === 0 ? (
        <Paper sx={{
          p: 6, textAlign: "center", borderRadius: 3,
          border: "1px dashed #cbd5e1", background: "#f8fafc",
        }}>
          <SmartToyIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 1.5 }} />
          <Typography sx={{ color: "#475569", fontSize: 14, fontWeight: 600, mb: 0.5 }}>
            {searchTerm
              ? `Sin resultados para "${searchTerm}"`
              : filter !== "all"
                ? "No hay chatbots en este filtro"
                : "Aún no tienes chatbots"}
          </Typography>
          {!searchTerm && filter === "all" && (
            <>
              <Typography sx={{ color: "#94a3b8", fontSize: 12, mb: 2 }}>
                Crea tu primer chatbot inteligente o usa una plantilla
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                <Button
                  variant="outlined" startIcon={<MenuBookRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setGaleriaOpen(true)}
                  sx={{
                    borderColor: "#c7d2fe", color: "#4f46e5",
                    textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5,
                    "&:hover": { borderColor: "#6366f1", bgcolor: "#eef2ff" },
                  }}
                >
                  Ver plantillas
                </Button>
                <Button
                  variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreateDialog(true)}
                  sx={{
                    background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                    textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5,
                    boxShadow: "0 4px 12px rgba(30,58,95,0.3)",
                    "&:hover": { background: "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)" },
                  }}
                >
                  Crear chatbot
                </Button>
              </Box>
            </>
          )}
          {(searchTerm || filter !== "all") && (
            <Button
              size="small" onClick={() => { setSearchTerm(""); setFilter("all"); }}
              sx={{ mt: 1, textTransform: "none", fontSize: 12 }}
            >
              Limpiar filtros
            </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{
          borderRadius: 3, overflow: "hidden",
          border: "1px solid #e2e8f0", background: "#fff",
        }}>
          {filteredAndSortedChatbots.map((cb) => (
            <ChatbotRow key={cb.id} chatbot={cb} {...cardActions(cb)} />
          ))}
        </Paper>
      )}

      {/* Galería de plantillas */}
      <PlantillasGaleria
        open={galeriaOpen}
        onClose={() => setGaleriaOpen(false)}
        chatbots={chatbots}
        onCloned={() => { fetchChatbots(); }}
      />

      {/* Dialog crear chatbot */}
      <StyledDialog open={createDialog} onClose={() => !creating && setCreateDialog(false)} title="Nuevo Chatbot Inteligente" icon={<SmartToyIcon />} variant="compact" maxWidth="xs" disableClose={creating}>
        <StyledDialog.Body compact>
          <Typography sx={{ color: "#64748b", fontSize: 12 }}>El chatbot se creará con el agente IA activado y todas las herramientas disponibles.</Typography>
          <TextField autoFocus fullWidth label="Nombre del chatbot" value={newChatbotName} onChange={(e) => setNewChatbotName(e.target.value)} placeholder="Ej: Asistente Clínica Dental" size="small" sx={inputStyles}
            onKeyDown={(e) => { if (e.key === "Enter" && newChatbotName.trim()) handleCreateChatbot(); }} />
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setCreateDialog(false)} disabled={creating} />
          <StyledDialog.PrimaryButton fullWidth onClick={handleCreateChatbot} disabled={!newChatbotName.trim()} loading={creating}>Crear Chatbot</StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* Dialog eliminar */}
      <StyledDialog open={deleteDialog} onClose={() => !deleting && setDeleteDialog(false)} title={deleteTarget ? "Eliminar Chatbot" : `Eliminar ${selectedIds.size} chatbot${selectedIds.size > 1 ? "s" : ""}`} icon={<DeleteIcon />} variant="compact" maxWidth="xs" disableClose={deleting}>
        <StyledDialog.Body compact>
          <Box sx={{ p: 2, bgcolor: "#fef2f2", borderRadius: 2, border: "1px solid #fecaca" }}>
            <Typography sx={{ fontSize: 13, color: "#991b1b", fontWeight: 600, mb: 0.5 }}>Esta acción no se puede deshacer</Typography>
            <Typography sx={{ fontSize: 12, color: "#dc2626" }}>
              {deleteTarget
                ? `Se eliminará "${deleteTarget.name}" y toda su configuración, base de conocimiento y sesiones.`
                : `Se eliminarán ${selectedIds.size} chatbot${selectedIds.size > 1 ? "s" : ""} y toda su configuración.`}
            </Typography>
          </Box>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => { setDeleteDialog(false); setDeleteTarget(null); }} disabled={deleting} />
          <Button fullWidth variant="contained" onClick={handleDelete} disabled={deleting}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, bgcolor: "#dc2626", py: 1, boxShadow: "0 4px 14px rgba(220,38,38,0.4)", "&:hover": { bgcolor: "#b91c1c" } }}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Eliminar"}
          </Button>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* Dialog duplicar */}
      <StyledDialog open={duplicateDialog} onClose={() => !duplicating && setDuplicateDialog(false)} title="Duplicar Chatbot" icon={<CopyIcon />} variant="compact" maxWidth="xs" disableClose={duplicating}>
        <StyledDialog.Body compact>
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            Se creará una copia de <strong>"{duplicateTarget?.name}"</strong> con toda su configuración y base de conocimiento.
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>La copia se creará desactivada y sin número de WhatsApp.</Typography>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => { setDuplicateDialog(false); setDuplicateTarget(null); }} disabled={duplicating} />
          <StyledDialog.PrimaryButton fullWidth onClick={handleDuplicate} loading={duplicating}>Duplicar</StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}

// ============================================================================
// Fila de chatbot — patrón visual idéntico a `PacienteFila` de EMR
// ============================================================================
function ChatbotRow({
  chatbot, selected, selectionActive, onSelect, onDelete, onDuplicate, onToggle, onClick,
  renamingId, renameValue, renaming, onRenameChange, onStartRename, onSaveRename, onCancelRename,
  isFavorite, onToggleFavorite, onExport,
}) {
  const [hovered, setHovered] = useState(false);
  const color = hashColor(chatbot.name);
  const inits = iniciales(chatbot.name);
  const lastActivity = timeAgo(chatbot.last_activity);
  const recentActive = isRecentlyActive(chatbot.last_activity);
  const showCheckbox = selectionActive || hovered;

  const convLevel = getUsageLevel(chatbot.conversaciones_totales, chatbot.plan_conversaciones);
  const tokenLevel = getUsageLevel(chatbot.tokens_usados, chatbot.plan_tokens);
  const usageLevel = convLevel === "critical" || tokenLevel === "critical" ? "critical"
    : convLevel === "warning" || tokenLevel === "warning" ? "warning" : null;

  // Badges contextuales (similar a Pacientes)
  const badges = [];
  if (!chatbot.agent_enabled) {
    badges.push({ label: "Inactivo", color: "#94a3b8", bg: "#f1f5f9" });
  }
  if (!chatbot.has_config) {
    badges.push({ label: "Sin configurar", color: "#d97706", bg: "#fef3c7" });
  }
  if (chatbot.whatsapp_number) {
    badges.push({ label: "WhatsApp", color: "#16a34a", bg: "#dcfce7", icon: <WhatsAppIcon sx={{ fontSize: 11 }} /> });
  }
  if (usageLevel) {
    badges.push({
      label: usageLevel === "critical" ? "Uso crítico" : "Uso alto",
      color: USAGE_COLORS[usageLevel],
      bg: usageLevel === "critical" ? "#fee2e2" : "#fef3c7",
      icon: <WarningIcon sx={{ fontSize: 11 }} />,
    });
  }

  const handleRowClick = () => {
    if (renamingId === chatbot.id) return;
    if (selectionActive) { onSelect(); return; }
    onClick();
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleRowClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.2,
        px: 2, py: 1.2,
        cursor: "pointer",
        borderBottom: "1px solid #f1f5f9",
        background: selected ? "#eef2ff" : "transparent",
        transition: "all 0.15s",
        "&:last-child": { borderBottom: "none" },
        "&:hover": {
          background: selected ? "#e0e7ff" : "#f8fafc",
          "& .row-actions": { opacity: 1 },
          "& .row-chevron": { transform: "translateX(2px)", color: "#1e3a5f" },
        },
      }}
    >
      {/* Checkbox (hover o cuando hay selección) */}
      <Fade in={showCheckbox} timeout={120}>
        <Box sx={{ flexShrink: 0, minWidth: 24, display: "flex", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={selected}
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
            sx={{ p: 0.25, color: "#cbd5e1", "&.Mui-checked": { color: "#1e3a5f" } }}
          />
        </Box>
      </Fade>

      {/* Avatar */}
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <Avatar sx={{
          width: 38, height: 38,
          bgcolor: color.bg, color: color.fg,
          fontSize: 13, fontWeight: 800,
        }}>
          {inits}
        </Avatar>
        {recentActive && (
          <Box sx={{
            position: "absolute", bottom: -2, right: -2,
            width: 10, height: 10, borderRadius: "50%",
            bgcolor: "#10b981", border: "2px solid #fff",
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(16,185,129,0.6)" },
              "70%": { boxShadow: "0 0 0 4px rgba(16,185,129,0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(16,185,129,0)" },
            },
            animation: "pulse 2s ease-in-out infinite",
          }} />
        )}
      </Box>

      {/* Info principal */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.2 }}>
          {renamingId === chatbot.id ? (
            <TextField
              autoFocus size="small" value={renameValue}
              onChange={(e) => onRenameChange(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") onSaveRename();
                if (e.key === "Escape") onCancelRename();
              }}
              onBlur={onSaveRename}
              onClick={(e) => e.stopPropagation()}
              disabled={renaming}
              inputProps={{ maxLength: 100, style: { fontSize: 14, fontWeight: 700, padding: "2px 6px" } }}
              sx={{ "& .MuiOutlinedInput-root": { height: 26, bgcolor: "#fff", borderRadius: 1, "& fieldset": { borderColor: "#1e3a5f" }, "&.Mui-focused fieldset": { borderColor: "#1e3a5f" } } }}
            />
          ) : (
            <>
              <Typography sx={{
                fontSize: 14, fontWeight: 700, color: "#1e293b",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                maxWidth: { xs: 180, sm: 280, md: 380 },
              }}>
                {chatbot.name}
              </Typography>
              <Tooltip title="Renombrar">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStartRename(); }}
                  sx={{
                    p: 0.25, color: "#cbd5e1",
                    opacity: hovered ? 1 : 0, transition: "opacity 0.15s",
                    "&:hover": { color: "#1e3a5f", bgcolor: "#eef2ff" },
                  }}>
                  <EditIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
              {chatbot.has_config && (
                <Chip
                  icon={<ConfigIcon sx={{ fontSize: 11 }} />}
                  label="Config" size="small"
                  sx={{
                    height: 18, fontSize: 9.5, fontWeight: 700,
                    background: "#e0e7ff", color: "#4338ca",
                    "& .MuiChip-icon": { color: "#4338ca", ml: 0.4 },
                    "& .MuiChip-label": { px: 0.7 },
                  }}
                />
              )}
              {badges.map((b, i) => (
                <Chip
                  key={i} label={b.label} size="small"
                  icon={b.icon || undefined}
                  sx={{
                    height: 18, fontSize: 9.5, fontWeight: 700,
                    background: b.bg, color: b.color,
                    "& .MuiChip-icon": { color: b.color, ml: 0.4 },
                    "& .MuiChip-label": { px: 0.7 },
                  }}
                />
              ))}
            </>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748b", fontSize: 11.5 }}>
          {chatbot.whatsapp_number && (
            <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
              {chatbot.whatsapp_number}
            </Box>
          )}
          {chatbot.sessions_count > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              {chatbot.whatsapp_number && <span>·</span>}
              <span>{chatbot.sessions_count} sesión{chatbot.sessions_count === 1 ? "" : "es"} activas</span>
            </Box>
          )}
          {chatbot.conversaciones_totales > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <span>·</span>
              <span>{chatbot.conversaciones_totales} conversación{chatbot.conversaciones_totales === 1 ? "" : "es"}</span>
            </Box>
          )}
        </Box>
      </Box>

      {/* Última actividad (derecha) */}
      <Box sx={{ textAlign: "right", mr: 1, flexShrink: 0, minWidth: 100, display: { xs: "none", sm: "block" } }}>
        {lastActivity ? (
          <>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Últ. actividad
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>
              {lastActivity}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 11, color: "#cbd5e1", fontStyle: "italic" }}>
            Sin actividad
          </Typography>
        )}
      </Box>

      {/* Toggle activo/inactivo */}
      <Tooltip title={!chatbot.has_config ? "Configura primero" : chatbot.agent_enabled ? "Desactivar agente IA" : "Activar agente IA"}>
        <span>
          <Switch
            size="small"
            checked={chatbot.agent_enabled}
            disabled={!chatbot.has_config}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            sx={{
              width: 36, height: 20, p: 0, flexShrink: 0,
              "& .MuiSwitch-switchBase": {
                p: "2px",
                "&.Mui-checked": {
                  transform: "translateX(16px)", color: "#fff",
                  "& + .MuiSwitch-track": { bgcolor: "#10b981", opacity: 1 },
                },
                "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.3 },
              },
              "& .MuiSwitch-thumb": { width: 16, height: 16 },
              "& .MuiSwitch-track": { borderRadius: 10, bgcolor: "#cbd5e1", opacity: 1 },
            }}
          />
        </span>
      </Tooltip>

      {/* Acciones (visibles en hover) */}
      <Box className="row-actions" sx={{
        display: "flex", alignItems: "center", gap: 0.3,
        opacity: 0, transition: "opacity 0.15s", flexShrink: 0,
      }}>
        <Tooltip title={isFavorite ? "Quitar de favoritos" : "Marcar como favorito"}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
            sx={{
              color: isFavorite ? "#f59e0b" : "#cbd5e1", p: 0.5,
              opacity: isFavorite ? 1 : undefined,
              "&:hover": { color: "#f59e0b", bgcolor: "#fef3c7" },
            }}>
            {isFavorite ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Exportar configuración (.json)">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onExport(); }}
            sx={{ color: "#cbd5e1", p: 0.5, "&:hover": { color: "#0ea5e9", bgcolor: "#e0f2fe" } }}>
            <ExportIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicar">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            sx={{ color: "#cbd5e1", p: 0.5, "&:hover": { color: "#6366f1", bgcolor: "#eef2ff" } }}>
            <CopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }}
            sx={{ color: "#cbd5e1", p: 0.5, "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" } }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chevron (señal de "click para entrar") */}
      <Box
        className="row-chevron"
        sx={{
          fontSize: 18, color: "#cbd5e1", flexShrink: 0,
          transition: "all 0.15s",
          fontWeight: 600,
        }}
      >
        ›
      </Box>
    </Box>
  );
}
